import { RichTextValue, SuggestionsItemData } from "@xapp/chat-widget-core";
import { WidgetFooterConfig, WidgetInputConfig, WidgetMenuConfig, WidgetMenuItems } from "@xapp/stentor-chat-widget";
import React, { FC, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ChatState } from "../../store/ChatState";
import { useSuggestions } from "../../utils/useSuggestions";
import AdminBar from "../AdminBar/AdminBar";
import { ChatBranding } from "../ChatBranding/ChatBranding";
import { ChatMenu } from "../ChatMenu";
import { DrawerBars } from "../DrawerBars";
import { Input } from "../Input";
import { Suggestions } from "../Suggestions/Suggestions";
import "./ChatFooter.scss";
import { useChatDispatch } from "../../utils/useChatDispatch";
import setVisualStatus from "../../store/actions/setVisualStatus";

export interface ChatFooterProps {
    /**
     * If this is an admin view, it will display the join field.
     */
    readonly isAdmin: boolean;
    readonly isChatting: boolean;

    readonly visible: boolean;

    readonly placeholder?: string;
    readonly sendButtonIcon?: string;
    readonly sendButtonIconHover?: string;
    readonly sendButtonIconDisabled?: string;
    readonly menuConfig?: WidgetMenuConfig;
    readonly footerConfig?: WidgetFooterConfig;
    readonly inputConfig?: WidgetInputConfig;
    readonly hasWsButton?: boolean;
    /**
     * When true, disables input entirely (e.g., for preview mode).
     */
    readonly disabled?: boolean;
    onSubmit(message: string): void;
    onChange(val: string): void;
    onFileUpload(event: React.DragEvent<HTMLInputElement>): void;
}

const ChatFooter: FC<ChatFooterProps> = props => {

    const { isAdmin, isChatting, visible, placeholder, sendButtonIcon, sendButtonIconHover, sendButtonIconDisabled, footerConfig, menuConfig, inputConfig, hasWsButton, disabled, onSubmit } = props;

    const innerDispatch = useChatDispatch();

    const [drawerOpen, setDrawerState] = useState<boolean>(false); // false initially

    const [suggestionSearch, setSuggestionSearch] = useState<string | undefined>();

    const contexts = useSelector<ChatState, readonly string[] | undefined>(state => state.activeContexts);

    const suggestions = useSuggestions(suggestionSearch, contexts);
    const menuPosition = menuConfig?.menuButtonLocation || "FOOTER";
    const showMenu = menuPosition === "FOOTER";
    const menuItemsRaw = menuConfig?.items;
    const menuItemsTabIndex = menuConfig?.itemsTabIndex;
    const menuButtonTabIndex = menuConfig?.button?.tabIndex;
    const menuItems = useMemo<readonly WidgetMenuItems[]>(() => menuItemsRaw ? menuItemsRaw : [], [menuItemsRaw]);

    const brandingEnabled = footerConfig?.branding?.enabled;
    const brandingText = footerConfig?.branding?.text;

    // If they are NOT an admin, automatically enabled the input
    const [enableInput, setEnableInput] = useState<boolean>(!isAdmin);

    const [input, setInput] = useState<RichTextValue>({
        text: "",
        formats: []
    });

    function toggleDrawer() {
        const newDrawer = !drawerOpen;
        setDrawerState(newDrawer);
        innerDispatch(setVisualStatus({
            drawer: newDrawer
        }));
    }

    function handleMenuItem(label: string) {
        setDrawerState(false);
        props.onSubmit(label);
    }

    function handleChange(val: RichTextValue) {
        setInput(val);
        props.onChange(val.text);
        setSuggestionSearch(val.text);
    }

    // Clears out suggestion and input text
    const handleSubmit = useCallback((message: RichTextValue) => {
        // Clears out the suggestions
        setSuggestionSearch("");
        // Clears out the input
        setInput({
            text: "",
            formats: []
        });
        onSubmit(message.text);
    }, [onSubmit]);

    const handleItemClick = useCallback((data: SuggestionsItemData) => {
        handleSubmit(data);
    }, [handleSubmit]);

    const handleItemUse = useCallback((data: SuggestionsItemData) => {
        setInput(data);
        setSuggestionSearch(data.text);
    }, []);

    const handleAdminJoin = (status: boolean) => {
        setEnableInput(status);
    };

    return (
        <div className="chat-footer background-footer" aria-label={menuItems.length ? "to open menu click a button above the rounded rectangle at the bottom of widget" : ""} aria-hidden={false}>
            {showMenu && menuItems.length ?
                <>
                    {drawerOpen ? <ChatMenu opened={drawerOpen} tabIndex={menuItemsTabIndex} onItemClick={handleMenuItem} items={menuItems} /> : <></>}
                    <div className="chat-footer__menu-icon">
                        <DrawerBars tabIndex={menuButtonTabIndex} onToggle={toggleDrawer} />
                    </div>
                </> : <></>}
            <Suggestions
                className="xappw-chat-footer__suggestions"
                data={suggestions.suggestions}
                index={suggestions.index}
                searchTerms={suggestionSearch}
                hasWsButton={hasWsButton}
                onItemClick={handleItemClick}
                onItemUse={handleItemUse}
            />
            {isAdmin && isChatting && visible && <AdminBar onAdminJoin={handleAdminJoin} />}

            <div style={{ pointerEvents: enableInput && !disabled ? "auto" : "none", opacity: enableInput ? 1 : 0.5 }}>
                <Input
                    addClass={"chat-footer__input " + (isChatting && visible ? "visible" : "")}
                    suggestion={suggestions.item}
                    value={input}
                    placeholder={placeholder}
                    sendButtonIcon={sendButtonIcon}
                    sendButtonIconHover={sendButtonIconHover}
                    sendButtonIconDisabled={sendButtonIconDisabled}
                    footerConfig={footerConfig}
                    inputConfig={inputConfig}
                    onSubmit={handleSubmit}
                    onChange={handleChange}
                    onSuggestionCommand={suggestions.execute}
                    // onFocus={this.inputOnFocus}
                    onFileUpload={props.onFileUpload}
                />
            </div>
            {brandingEnabled && brandingText && <ChatBranding text={brandingText} />}
        </div>
    );
};

export default ChatFooter;